import React, { useEffect } from "react";

import styled from "@emotion/styled";
import { useDebounce } from "use-debounce";
import shallow from "zustand/shallow";

import pushToAnalytics from "../../../../utils/analytics";
import { Shadow } from "../../../../utils/BreakpointHelpers";
import { deleteUrlParam, setUrlParam } from "../../../../utils/urlParams";
import NewInput from "../../../shared/forms/NewInput";
import NewSelect from "../../../shared/forms/NewSelect";
import useStore, { SearchFilter } from "../store";

const SearchContainer = styled.div`
    z-index: 5;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.colors.white.toString()};
`;

const SearchSuggestionsContainer = styled.div`
    background-color: ${({ theme }) => theme.colors.white.toString()};
    position: absolute;
    top: 100px;
    min-width: 100%;
    padding: 10px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    z-index: 2;
    ${Shadow};

    max-height: 50vh;
    overflow-y: auto;

    @media (min-width: 1024px) {
            width: auto;
            white-space: nowrap;
        }
    }
`;
const SearchItem = styled.div`
    :hover {
        background-color: ${({ theme }) => theme.colors.grey.toString()};
    }

    cursor: pointer;
`;

type iSearchSuggestions = {
    searchResults: any;
    loadingSearch: boolean;
    setVehicle: (value: any) => void;
};
const SearchSuggestions = ({
    searchResults,
    loadingSearch,
    setVehicle,
}: iSearchSuggestions) => {
    const handleClick = (item: any) => {
        const element = document.getElementById("oil-finder-product-list");

        if (element) {
            const topPosition =
                element.getBoundingClientRect().top + window.scrollY - 100;
            window.scrollTo({ top: topPosition, behavior: "smooth" });
        }
        setVehicle(item);
    };

    if ((searchResults && searchResults.length > 0) || loadingSearch) {
        return (
            <SearchSuggestionsContainer>
                {searchResults &&
                    searchResults.length > 0 &&
                    searchResults.map((item: any) => (
                        <SearchItem
                            key={item.id}
                            onClick={() => handleClick(item)}
                        >
                            {item.result}
                        </SearchItem>
                    ))}
                {loadingSearch && <div>Loading...</div>}
            </SearchSuggestionsContainer>
        );
    }
    return null;
};

export const Select = () => {
    const [
        searchFilter,
        setSearchFilter,
        setSearchResults,
        setSearch,
        setCategory,
    ] = useStore(
        (state) => [
            state.searchFilter,
            state.setSearchFilter,
            state.setSearchResults,
            state.setSearch,
            state.setCategory,
        ],
        shallow
    );

    const options = [
        {
            text: "Nach Name und Produktnummer",
            value: SearchFilter.ALLES,
        },
        {
            text: "In Kategorie",
            value: SearchFilter.NACH_CATEGORY,
        },
        {
            text: "Nach Fahrzeugen",
            value: SearchFilter.NACH_FAHRZEUG,
        },
        /*{
            text: "Nach Schlüsselnummer",
            value: SearchFilter.NACH_SCHLUESSELNUMMER,
        },*/
    ];

    const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSearchFilter(event.target.value as SearchFilter);
        setSearchResults(null);
        setSearch("");
        if (event.target.value !== SearchFilter.NACH_CATEGORY) {
            setCategory(null);
            deleteUrlParam("category");
        }
    };

    return (
        <NewSelect
            placeholder="Suche"
            options={options}
            value={searchFilter}
            onChange={handleSelect}
        />
    );
};

const Search = () => {
    const [
        setCategory,
        setSearch,
        search,
        setPage,
        getData,
        searchFilter,
        searchResults,
        getSearchResults,
        loadingSearch,
        setVehicle,
        vehicle,
        setSearchResults,
        clearSearch,
        setNeedsReload,
        warnMessage,
        setWarnMessage,
    ] = useStore(
        (state) => [
            state.setCategory,
            state.setSearch,
            state.search,
            state.setPage,
            state.getData,
            state.searchFilter,
            state.searchResults,
            state.getSearchResults,
            state.loadingSearch,
            state.setVehicle,
            state.vehicle,
            state.setSearchResults,
            state.clearSearch,
            state.setNeedsReload,
            state.warnMessage,
            state.setWarnMessage,
        ],
        shallow
    );

    const [debouncedSearch] = useDebounce(search, 500);

    useEffect(() => {
        if (debouncedSearch) {
            onSearch();
        } else {
            clearSearch();
        }
    }, [debouncedSearch]);

    const onLocalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
        setUrlParam("search", event.target.value);
        setWarnMessage(null);
    };

    const onSearch = () => {
        if (!search?.trim() && searchFilter === SearchFilter.NACH_FAHRZEUG) {
            setWarnMessage("Bitte Fahrzeugtypen eintragen.");
            return;
        }
        if (search.trim().length > 0 && search.trim().length < 3) {
            setWarnMessage("Bitte mindestens drei Buchstaben eingeben.");
            return;
        }
        if (search) {
            pushToAnalytics("Schmierstoffsuche", {
                searchTerm: search,
                searchType: searchFilter,
            });
        }
        if (searchFilter !== SearchFilter.NACH_CATEGORY) {
            void setCategory(null);
            void setPage(1);
            deleteUrlParam("page");
            deleteUrlParam("category");
        }
        if (
            searchFilter === SearchFilter.NACH_FAHRZEUG ||
            searchFilter === SearchFilter.NACH_SCHLUESSELNUMMER
        ) {
            getSearchResults();
        } else {
            void getData();
        }
    };

    const handleSetVehicle = (vehicle: any) => {
        setVehicle(vehicle);
        // setSearch(vehicle.result);
        setSearchResults(null);
        setNeedsReload(true);
    };

    return (
        <SearchContainer>
            <Select />
            <NewInput
                placeholder={"Suche"}
                value={search ?? ""}
                onChange={onLocalChange}
                handleClick={onSearch}
                hasError={!!warnMessage}
            />
            {warnMessage && <div style={{ color: "red" }}>{warnMessage}</div>}
            <SearchSuggestions
                searchResults={searchResults}
                loadingSearch={loadingSearch}
                setVehicle={handleSetVehicle}
            />
        </SearchContainer>
    );
};

export default Search;
